import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, CreditCard, X, Package, Car, User, Settings, Building, FileText, Mail, KeyRound, Trophy } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/currencyUtils';

interface TrackBooking {
  id: string;
  track_type: 'drag' | 'drift';
  booking_type: 'individual' | 'team' | 'exclusive';
  time_slot: 'morning' | 'afternoon' | 'evening' | 'night';
  booking_date: string;
  duration_hours: number;
  use_traction_compound: boolean;
  total_amount: number;
  status: string;
  payment_status: string;
  special_requests?: string;
  start_time?: string;
  end_time?: string;
  add_ons: AddOnService[];
}

interface AddOnService {
  id: string;
  name: string;
  description: string;
  price: number;
}

interface DriverProfile {
  id: string;
  vehicle_type: 'auto' | 'motorcycle';
  is_complete: boolean;
  personal_info: any;
  vehicle_info: any;
  driver_info: any;
}

export default function Profile() {
  const { user, userProfile, updateProfile, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<TrackBooking[]>([]);
  const [driverProfiles, setDriverProfiles] = useState<DriverProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPromotorProfileModal, setShowPromotorProfileModal] = useState(false);
  const [promotorForm, setPromotorForm] = useState({
    company_name: userProfile?.company_name || '',
    crib_number: userProfile?.crib_number || '',
    kvk_number: userProfile?.kvk_number || '',
  });
  const [formErrors, setFormErrors] = useState({
    crib_number: '',
    kvk_number: ''
  });

  useEffect(() => {
    if (user) {
      fetchBookings();
      fetchDriverProfiles();
    }
  }, [user]);

  // Update form when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setPromotorForm({
        company_name: userProfile.company_name || '',
        crib_number: userProfile.crib_number || '',
        kvk_number: userProfile.kvk_number || '',
      });
    }
  }, [userProfile]);

  // Helper function to format date for display (avoiding timezone issues)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (time: string) => {
    if (!time) return '';
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper function to get time display
  const getTimeDisplay = (booking: TrackBooking) => {
    if (booking.start_time && booking.end_time) {
      return `${formatTime(booking.start_time)} - ${formatTime(booking.end_time)}`;
    }
    if (booking.time_slot) {
      return booking.time_slot.charAt(0).toUpperCase() + booking.time_slot.slice(1);
    }
    return '';
  };

  const fetchBookings = async () => {
    try {
      // Fetch bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('track_bookings')
        .select(`
          *,
          add_ons:booking_add_ons(
            add_on:add_on_services(
              id,
              name,
              description,
              price
            )
          )
        `)
        .eq('user_id', user?.id)
        .order('booking_date', { ascending: false });

      if (bookingsError) throw bookingsError;

      // Transform the data to include add-ons in a cleaner format
      const transformedBookings = bookingsData?.map(booking => ({
        ...booking,
        add_ons: booking.add_ons
          .map(addon => addon.add_on)
          .filter(Boolean)
      }));

      setBookings(transformedBookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', user?.id);

      if (data && !error) {
        setDriverProfiles(data);
      }
    } catch (error) {
      console.error('Error fetching driver profiles:', error);
    }
  };

  const validatePromotorForm = () => {
    let isValid = true;
    const errors = {
      crib_number: '',
      kvk_number: ''
    };

    // Validate CRIB number (exactly 9 digits)
    if (!promotorForm.crib_number) {
      errors.crib_number = 'CRIB number is required';
      isValid = false;
    } else if (!/^\d{9}$/.test(promotorForm.crib_number)) {
      errors.crib_number = 'CRIB must be exactly 9 digits';
      isValid = false;
    }

    // Validate KVK number (6-8 digits)
    if (!promotorForm.kvk_number) {
      errors.kvk_number = 'KVK number is required';
      isValid = false;
    } else if (!/^\d{6,8}$/.test(promotorForm.kvk_number)) {
      errors.kvk_number = 'KVK must be 6 to 8 digits';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handlePromotorProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePromotorForm()) {
      return;
    }

    try {
      await updateProfile({
        company_name: promotorForm.company_name,
        crib_number: promotorForm.crib_number,
        kvk_number: promotorForm.kvk_number,
      });
      
      toast.success('Profile updated successfully');
      setShowPromotorProfileModal(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'user': return 'User';
      case 'driver': return 'Driver';
      case 'promotor': return 'Promotor';
      case 'admin': return 'Administrator';
      case 'worker': return 'Worker';
      default: return role;
    }
  };

  const getDriverProfileSummary = () => {
    if (driverProfiles.length === 0) return 'No registrations';
    
    const autoProfile = driverProfiles.find(p => p.vehicle_type === 'auto');
    const motorcycleProfile = driverProfiles.find(p => p.vehicle_type === 'motorcycle');
    
    const registrations = [];
    if (autoProfile) {
      registrations.push(`Auto (${autoProfile.is_complete ? 'Complete' : 'In Progress'})`);
    }
    if (motorcycleProfile) {
      registrations.push(`Motorcycle (${motorcycleProfile.is_complete ? 'Complete' : 'In Progress'})`);
    }
    
    return registrations.join(', ');
  };

  // Check if a booking date is in the future
  const isFutureBooking = (bookingDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = bookingDate.split('-');
    const booking = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return booking >= today;
  };

  const isPromotor = userProfile?.role === 'promotor';
  const isDriver = userProfile?.role === 'driver';
  const profileIncomplete = isPromotor && !isProfileComplete();

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8">
          Your Profile
        </h1>

        {/* User Info Card */}
        <div className="bg-zinc-900 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Account Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-gray-400">Name</p>
              <p className="text-white">{userProfile?.full_name}</p>
            </div>
            <div className="relative">
              <p className="text-gray-400">Email</p>
              <div className="flex items-center gap-2">
                <p className="text-white">{userProfile?.email}</p>
                <Link 
                  to="/update-email" 
                  className="text-xs text-primary-500 hover:text-primary-400 absolute right-0 top-0"
                >
                  Change
                </Link>
              </div>
            </div>
            <div>
              <p className="text-gray-400">Phone</p>
              <p className="text-white">{userProfile?.phone}</p>
            </div>
            <div>
              <p className="text-gray-400">Account Type</p>
              <p className="text-white">{getRoleDisplayName(userProfile?.role || '')}</p>
            </div>
            <div>
              <p className="text-gray-400">Member Since</p>
              <p className="text-white">
                {userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString() : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-400">Password</p>
              <div className="flex items-center gap-2">
                <p className="text-white">••••••••</p>
                <Link 
                  to="/update-password" 
                  className="text-xs text-primary-500 hover:text-primary-400"
                >
                  Change
                </Link>
              </div>
            </div>
            
            {isPromotor && (
              <div className="md:col-span-3 mt-2">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-400">Company</p>
                    <p className="text-white">{userProfile?.company_name || 'Not set'}</p>
                    
                    {profileIncomplete && (
                      <div className="mt-2 p-3 bg-amber-900 border border-amber-700 rounded-lg">
                        <p className="text-amber-200 text-sm">
                          Please complete your promotor profile with CRIB and KVK numbers to enable booking.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setShowPromotorProfileModal(true)}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <Building className="h-4 w-4" />
                    {profileIncomplete ? 'Complete Profile' : 'Edit Company Info'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account Management Links */}
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <h3 className="text-lg font-semibold text-white mb-3">Account Management</h3>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/update-email" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm"
              >
                <Mail className="h-4 w-4" />
                Change Email
              </Link>
              <Link 
                to="/update-password" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm"
              >
                <KeyRound className="h-4 w-4" />
                Change Password
              </Link>
            </div>
          </div>
        </div>

        {/* Driver Profile Status (only for drivers) */}
        {isDriver && (
          <div className="bg-zinc-900 rounded-lg p-6 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Driver Registrations</h2>
                <p className="text-gray-400">{getDriverProfileSummary()}</p>
                
                {driverProfiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {driverProfiles.map((profile) => (
                      <div key={profile.id} className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          profile.is_complete 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {profile.vehicle_type === 'auto' ? 'Auto' : 'Motorcycle'}
                        </span>
                        <span className="text-gray-400 text-sm">
                          {profile.is_complete ? 'Complete' : 'In Progress'}
                        </span>
                        {profile.vehicle_info?.vehicleBrand && profile.vehicle_info?.model && (
                          <span className="text-gray-500 text-sm">
                            {profile.vehicle_info.vehicleBrand} {profile.vehicle_info.model}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/driver-profile')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Car className="h-4 w-4" />
                {driverProfiles.length > 0 ? 'Manage Registrations' : 'Start Registration'}
              </button>
            </div>
          </div>
        )}

        {/* Upcoming Bookings */}
        <div className="bg-zinc-900 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Upcoming Bookings</h2>
            <button
              onClick={() => setShowHistoryModal(true)}
              className="text-primary-500 hover:text-primary-400 text-sm"
            >
              View All Bookings
            </button>
          </div>
          
          {bookings.filter(b => isFutureBooking(b.booking_date)).length > 0 ? (
            <div className="space-y-4">
              {bookings
                .filter(b => isFutureBooking(b.booking_date))
                .map((booking) => (
                  <div key={booking.id} className="bg-zinc-800 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">
                            {booking.track_type === 'drag' ? 'Drag Strip' : 'Drift Track'} - {booking.duration_hours}h
                          </h3>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          Date: {formatDateForDisplay(booking.booking_date)}
                        </p>
                        <p className="text-gray-400 text-sm">
                          Time: {getTimeDisplay(booking)}
                        </p>
                        <p className="text-gray-400 text-sm">
                          Amount: {formatCurrency(booking.total_amount)}
                        </p>
                      </div>
                    </div>

                    {/* Add-ons */}
                    {booking.add_ons && booking.add_ons.length > 0 && (
                      <div className="mt-4 border-t border-zinc-700 pt-4">
                        <h4 className="text-sm font-semibold text-white mb-2">Add-on Services:</h4>
                        <div className="space-y-2">
                          {booking.add_ons.map((addon) => (
                            <div key={addon.id} className="flex justify-between text-sm">
                              <span className="text-gray-400">{addon.name}</span>
                              <span className="text-gray-400">{formatCurrency(addon.price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {booking.special_requests && (
                      <div className="mt-4 border-t border-zinc-700 pt-4">
                        <h4 className="text-sm font-semibold text-white mb-2">Special Requests:</h4>
                        <p className="text-gray-400 text-sm">{booking.special_requests}</p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">
              No upcoming bookings. {isPromotor ? 'Book track time to get started!' : 'Check the Events page for upcoming races!'}
            </p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {isPromotor && (
            <button 
              onClick={() => {
                if (profileIncomplete) {
                  toast.error('Please complete your promotor profile before booking');
                  setShowPromotorProfileModal(true);
                  return;
                }
                navigate('/services');
              }}
              className={`bg-zinc-900 p-6 rounded-lg hover:bg-zinc-800 transition-colors ${
                profileIncomplete ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <Calendar className="h-8 w-8 text-primary-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Book Track</h3>
              <p className="text-gray-400">Schedule your next track session</p>
            </button>
          )}

          <button 
            onClick={() => setShowHistoryModal(true)}
            className="bg-zinc-900 p-6 rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <Clock className="h-8 w-8 text-primary-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Booking History</h3>
            <p className="text-gray-400">View your past bookings</p>
          </button>

          {isDriver && (
            <button 
              onClick={() => navigate('/driver-profile')}
              className="bg-zinc-900 p-6 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <Car className="h-8 w-8 text-primary-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Driver Registration</h3>
              <p className="text-gray-400">
                {driverProfiles.length > 0 ? 'Manage your registrations' : 'Complete your registration'}
              </p>
            </button>
          )}

          <button 
            onClick={() => navigate('/tournament-results')}
            className="bg-zinc-900 p-6 rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <Trophy className="h-8 w-8 text-primary-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Tournament Results</h3>
            <p className="text-gray-400">View your tournament performance and points</p>
          </button>

          {!isDriver && !isPromotor && (
            <button 
              onClick={() => navigate('/events')}
              className="bg-zinc-900 p-6 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <Calendar className="h-8 w-8 text-primary-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Upcoming Events</h3>
              <p className="text-gray-400">View upcoming racing events</p>
            </button>
          )}
        </div>

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Booking History</h2>
                <button onClick={() => setShowHistoryModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              
              {bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="bg-zinc-800 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-white">
                              {booking.track_type === 'drag' ? 'Drag Strip' : 'Drift Track'} - {booking.duration_hours}h
                            </h3>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                              {booking.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">
                            Date: {formatDateForDisplay(booking.booking_date)}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Time: {getTimeDisplay(booking)}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Amount: {formatCurrency(booking.total_amount)}
                          </p>
                        </div>
                      </div>

                      {/* Add-ons */}
                      {booking.add_ons && booking.add_ons.length > 0 && (
                        <div className="mt-4 border-t border-zinc-700 pt-4">
                          <h4 className="text-sm font-semibold text-white mb-2">Add-on Services:</h4>
                          <div className="space-y-2">
                            {booking.add_ons.map((addon) => (
                              <div key={addon.id} className="flex justify-between text-sm">
                                <span className="text-gray-400">{addon.name}</span>
                                <span className="text-gray-400">{formatCurrency(addon.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {booking.special_requests && (
                        <div className="mt-4 border-t border-zinc-700 pt-4">
                          <h4 className="text-sm font-semibold text-white mb-2">Special Requests:</h4>
                          <p className="text-gray-400 text-sm">{booking.special_requests}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">
                  No booking history found.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Promotor Profile Modal */}
        {showPromotorProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Promotor Profile</h2>
                <button onClick={() => setShowPromotorProfileModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              
              <form onSubmit={handlePromotorProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={promotorForm.company_name}
                    onChange={(e) => setPromotorForm({ ...promotorForm, company_name: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    CRIB Number *
                  </label>
                  <input
                    type="text"
                    value={promotorForm.crib_number}
                    onChange={(e) => setPromotorForm({ ...promotorForm, crib_number: e.target.value })}
                    required
                    className={`w-full bg-zinc-800 border ${
                      formErrors.crib_number ? 'border-primary-500' : 'border-zinc-700'
                    } rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none`}
                    placeholder="9 digits"
                  />
                  {formErrors.crib_number && (
                    <p className="mt-1 text-sm text-primary-500">{formErrors.crib_number}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">CRIB number must be exactly 9 digits</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    KVK Number *
                  </label>
                  <input
                    type="text"
                    value={promotorForm.kvk_number}
                    onChange={(e) => setPromotorForm({ ...promotorForm, kvk_number: e.target.value })}
                    required
                    className={`w-full bg-zinc-800 border ${
                      formErrors.kvk_number ? 'border-primary-500' : 'border-zinc-700'
                    } rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none`}
                    placeholder="6-8 digits"
                  />
                  {formErrors.kvk_number && (
                    <p className="mt-1 text-sm text-primary-500">{formErrors.kvk_number}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">KVK number must be 6 to 8 digits</p>
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold"
                  >
                    Save Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}